import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MethodConfig } from "../../../config/Methods.config";
import { MethodsInput } from "../../../types/model/methodsInput";
import { getDisplayValue } from "./MethodRunner.utils";
import SummaryFieldInput from "./SummaryFieldInput";

interface MethodSummaryCardProps<TInputs> {
  captureId: number;
  selectedInput: MethodsInput;
  inputs: (keyof MethodsInput)[];
  config: MethodConfig<TInputs>;
  canEdit: boolean;
  isEditingSummary: boolean;
  savingSummary: boolean;
  currentCalculations: any[];
  summaryControl: Control<any>;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onCalculate: () => void;
  summaryRef: React.RefObject<HTMLDivElement>;
  resultsRef: React.RefObject<HTMLDivElement>;
}

export default function MethodSummaryCard<TInputs>({
  captureId,
  selectedInput,
  inputs,
  config,
  canEdit,
  isEditingSummary,
  savingSummary,
  currentCalculations,
  summaryControl,
  onEdit,
  onCancelEdit,
  onSave,
  onCalculate,
  summaryRef,
  resultsRef,
}: Readonly<MethodSummaryCardProps<TInputs>>) {
  const { t } = useTranslation();
  const hasExtraInputs = !!config.ExtraInputsComponent;

  return (
    <Stack spacing={3} ref={summaryRef}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700 }}>
          {t("calculations.methods.sheet-summary", { name: selectedInput.name })}
        </Typography>

        {canEdit && inputs.length > 0 && (
          isEditingSummary ? (
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={onCancelEdit} disabled={savingSummary}>
                {t("generic.cancel")}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={onSave}
                disabled={savingSummary}
                startIcon={savingSummary ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                {t("generic.save")}
              </Button>
            </Stack>
          ) : (
            <Button size="small" startIcon={<EditOutlinedIcon fontSize="small" />} onClick={onEdit}>
              {t("generic.edit")}
            </Button>
          )
        )}
      </Stack>

      {inputs.length > 0 && (
        <Box
          sx={{
            border: "2px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
          }}
        >
          {inputs.map((field) => {
            const fieldName = String(field);
            const value = selectedInput[field];

            return (
              <Box
                key={fieldName}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  alignItems: "center",
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>
                  {t(`calculations.methods.inputs.${fieldName}`)}
                </Typography>

                {isEditingSummary ? (
                  <SummaryFieldInput name={fieldName} originalValue={value} control={summaryControl} />
                ) : (
                  <Typography>{getDisplayValue(value, t)}</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {config.ExtraInputsComponent && (
        <Box sx={{ mt: 2 }}>
          <config.ExtraInputsComponent canEdit={canEdit} />
        </Box>
      )}

      {canEdit && hasExtraInputs && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={onCalculate}>
            {currentCalculations.length > 0 ? t("generic.recalculate") : t("generic.calculate")}
          </Button>
        </Box>
      )}

      {currentCalculations.length > 0 && (
        <Box ref={resultsRef}>
          <config.ResultsComponent
            calculations={currentCalculations.flat()}
            captureId={captureId}
          />
        </Box>
      )}
    </Stack>
  );
}